/**
 * Painel do ambiente no setor: o que a planilha diz, onde ele está dentro do
 * setor (subsetor/posto), o status de produção, os prazos do app, as PEÇAS
 * DESMEMBRADAS dele, as tarefas, o histórico de comentários (o mesmo das
 * páginas do Legado) e a linha do tempo completa (planilha + app).
 *
 * Transições que pedem campo extra (pausar com motivo, prazo com motivo)
 * coletam no próprio painel, sem tela separada (padrão Transition Form).
 */
import React, { useEffect, useId, useMemo, useState } from "react";
import { FiExternalLink, FiPlus, FiScissors } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import HistoricoAmbiente from "../../../components/HistoricoAmbiente";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { listarEventosAmbiente } from "../../../services/mapaProducao";
import { formatarDataLocal, plural } from "../../../utils/dinheiro";
import { F } from "../../../utils/gerencialFields";
import { toDate } from "../../../utils/status";
import { montarTimelineAmbiente } from "../dados/timeline";
import {
  ROTULO_PRIORIDADE,
  ROTULO_STATUS_AMBIENTE,
  ROTULO_STATUS_PECA,
  ROTULO_STATUS_TAREFA,
  TOM_PRIORIDADE,
  TOM_STATUS_AMBIENTE,
  TOM_STATUS_PECA,
  TOM_STATUS_TAREFA,
  acoesDoAmbiente,
} from "../dados/vocabulario";
import { CamposLugar } from "./formularios";
import { pessoasDaDemanda, textoDaOrigem, textoDoPrazo, textoDoTempoNoSetor } from "./modelo";
import { Acoes, Ajuda, Campo, Campos, Dados, ItemLista, Lista, Secao, Selos, Vazio } from "./styles";
import { ListaTimeline } from "./Timeline";
import { ErroDaAcao, PillStatus, useErroDeAcao } from "./ui";

const SUCESSO_DA_ACAO = {
  iniciar: "Produção iniciada.",
  pausar: "Produção pausada.",
  retomar: "Produção retomada.",
  aguardar: "Ambiente voltou para aguardando.",
};

const dataOuTraco = (d) => (d ? formatarDataLocal(d) : "Sem data");

const SEM_HISTORICO = { eventos: [], erro: null };

/**
 * Histórico do ambiente. O que está guardado anda SEMPRE junto da chave de
 * quem foi lido: abrir outro ambiente — ou uma linha sem pedido/ambiente,
 * em que `chave` é nula e nada é buscado — não pode mostrar os eventos do
 * anterior. Reler o MESMO ambiente (`gatilho`, depois de comentar ou de uma
 * escrita) mantém a lista à vista até a nova chegar.
 */
function useEventosDoAmbiente(chave, aberto, gatilho) {
  const { request } = useAuth();
  const [lido, setLido] = useState({ chave: null, ...SEM_HISTORICO });
  useEffect(() => {
    if (!aberto || !chave) return undefined;
    let vivo = true;
    Promise.resolve()
      .then(() => listarEventosAmbiente(request, chave))
      .then((lista) => {
        if (vivo) setLido({ chave, eventos: Array.isArray(lista) ? lista : [], erro: null });
      })
      .catch((e) => {
        if (!vivo) return;
        const erro = e?.message || "Não foi possível ler o histórico.";
        setLido((atual) => ({ chave, eventos: atual.chave === chave ? atual.eventos : [], erro }));
      });
    return () => {
      vivo = false;
    };
  }, [request, chave, aberto, gatilho]);
  return chave && lido.chave === chave ? { eventos: lido.eventos, erro: lido.erro } : SEM_HISTORICO;
}

function Resumo({ demanda, indice, hoje }) {
  const prazo = textoDoPrazo(demanda.prazoEfetivo, hoje);
  const pessoas = pessoasDaDemanda(demanda, indice);
  const tempo = textoDoTempoNoSetor(demanda);
  const reguaPrazo = textoDaOrigem(demanda.origemPrazo);
  const reguaPrioridade = `Prioridade ${textoDaOrigem(demanda.origemPrioridade, { feminino: true })}`;
  return (
    <Secao aria-label="Resumo">
      <Selos>
        <PillStatus tom={TOM_STATUS_AMBIENTE[demanda.statusApp]}>
          {ROTULO_STATUS_AMBIENTE[demanda.statusApp]}
        </PillStatus>
        {demanda.atrasada && <PillStatus tom="danger">{prazo.texto}</PillStatus>}
        <PillStatus tom={TOM_PRIORIDADE[demanda.prioridade]} ponto={false} title={reguaPrioridade}>
          {ROTULO_PRIORIDADE[demanda.prioridade]}
        </PillStatus>
        {demanda.movidoAMao && (
          <PillStatus
            tom="info"
            ponto={false}
            title={`A planilha coloca este ambiente em ${demanda.setorNomePlanilha || "outro setor"}.`}
          >
            Movido à mão
          </PillStatus>
        )}
        {demanda.servico && <PillStatus tom="info" ponto={false}>Serviço</PillStatus>}
        {demanda.aguardandoEtapa && (
          <PillStatus
            tom="warning"
            ponto={false}
            title="Esta etapa não tem sala no mapa: o ambiente espera aqui, na sala vizinha do fluxo."
          >
            Aguardando {demanda.aguardandoEtapa}
          </PillStatus>
        )}
        {demanda.pendencias.map((p) => (
          <PillStatus key={p.codigo} tom="warning" ponto={false}>{p.texto}</PillStatus>
        ))}
      </Selos>
      {demanda.motivoPausa && <p className="nota">Pausado: {demanda.motivoPausa}</p>}
      {demanda.alocacaoAntiga && (
        <p className="nota">
          O apontamento anterior era de outra etapa; a planilha já andou este ambiente. O primeiro
          apontamento aqui começa do zero neste setor.
        </p>
      )}
      <Dados>
        <div>
          <dt>Etapa da planilha</dt>
          <dd>{demanda.etapaRotulo || "Sem etapa"}</dd>
        </div>
        <div>
          <dt>Prazo que vale</dt>
          <dd>
            {dataOuTraco(demanda.prazoEfetivo)}
            {reguaPrazo ? ` (${reguaPrazo})` : ""}
          </dd>
        </div>
        <div>
          <dt>Prazo da planilha</dt>
          <dd>{dataOuTraco(demanda.prazoPlanilha)}</dd>
        </div>
        <div>
          <dt>Previsão do gestor</dt>
          <dd>{dataOuTraco(demanda.prevista)}</dd>
        </div>
        <div>
          <dt>No setor</dt>
          <dd>{tempo || "Sem data de chegada"}</dd>
        </div>
        <div>
          <dt>Responsáveis</dt>
          <dd>{pessoas.length ? pessoas.map((p) => p.nome).join(", ") : "Ninguém apontado"}</dd>
        </div>
      </Dados>
    </Secao>
  );
}

function AcoesDeStatus({ demanda, podeOperar, acoes, rodar }) {
  const [pendente, setPendente] = useState(null);
  const [motivo, setMotivo] = useState("");
  const disponiveis = acoesDoAmbiente(demanda.statusApp, { podeOperar });
  useEffect(() => {
    setPendente(null);
    setMotivo("");
  }, [demanda.statusApp]);
  if (!disponiveis.length) return null;

  const executar = (acao, texto = "") => rodar(() => acoes.acaoAmbiente(demanda, acao.acao, texto));

  return (
    <Secao aria-label="Produção">
      <h3>Produção</h3>
      {pendente ? (
        <>
          <Campo>
            Motivo (opcional)
            <textarea
              value={motivo}
              maxLength={500}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: falta material, aguardando ferragem"
            />
          </Campo>
          <Acoes>
            <AsyncButton
              silentError
              successMessage={SUCESSO_DA_ACAO[pendente.acao]}
              onClick={() => executar(pendente, motivo.trim()).then(() => setPendente(null))}
            >
              Confirmar: {pendente.rotulo.toLowerCase()}
            </AsyncButton>
            <button type="button" className="btn btn--secondary" onClick={() => setPendente(null)}>
              Voltar
            </button>
          </Acoes>
        </>
      ) : (
        <Acoes>
          {disponiveis.map((acao) =>
            acao.pedeMotivo ? (
              <button
                key={acao.acao}
                type="button"
                className="btn btn--secondary"
                onClick={() => setPendente(acao)}
              >
                {acao.rotulo}
              </button>
            ) : (
              <AsyncButton
                key={acao.acao}
                variant={acao.acao === "iniciar" || acao.acao === "retomar" ? "primary" : "secondary"}
                silentError
                successMessage={SUCESSO_DA_ACAO[acao.acao]}
                onClick={() => executar(acao)}
              >
                {acao.rotulo}
              </AsyncButton>
            )
          )}
        </Acoes>
      )}
    </Secao>
  );
}

function LugarNoSetor({ demanda, estado, podeOperar, acoes, rodar }) {
  const [lugar, setLugar] = useState({ subsetorId: demanda.subsetorId, postoId: demanda.postoId });
  const versao = demanda.versaoAlocacao;
  useEffect(() => {
    setLugar({ subsetorId: demanda.subsetorId, postoId: demanda.postoId });
  }, [demanda.chaveLinha, versao, demanda.subsetorId, demanda.postoId]);
  const mudou = lugar.subsetorId !== demanda.subsetorId || lugar.postoId !== demanda.postoId;
  return (
    <Secao aria-label="Lugar no setor">
      <h3>Lugar no setor</h3>
      <CamposLugar
        setorId={demanda.setorId}
        estado={estado}
        subsetorId={lugar.subsetorId}
        postoId={lugar.postoId}
        onChange={setLugar}
        desabilitado={!podeOperar}
      />
      {podeOperar && (
        <Acoes>
          <AsyncButton
            silentError
            disabled={!mudou}
            successMessage="Ambiente distribuído."
            onClick={() => rodar(() => acoes.alocar(demanda, lugar))}
          >
            Salvar lugar
          </AsyncButton>
        </Acoes>
      )}
    </Secao>
  );
}

function Prazos({ demanda, podeOperar, acoes, rodar }) {
  const inicial = () => ({
    prevista: demanda.alocacao?.data_prevista || "",
    limite: demanda.alocacao?.data_limite || "",
    motivo: "",
  });
  const [form, setForm] = useState(inicial);
  const idAjuda = useId();
  const versao = demanda.versaoAlocacao;
  useEffect(() => {
    setForm(inicial());
    // Recomeça o formulário quando a alocação muda (outro gestor salvou).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demanda.chaveLinha, versao]);
  const mudou =
    form.prevista !== (demanda.alocacao?.data_prevista || "") || form.limite !== (demanda.alocacao?.data_limite || "");
  const mudar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  return (
    <Secao aria-label="Prazos">
      <h3>Prazos neste setor</h3>
      <Campos $min={160}>
        <Campo>
          Previsão de término
          <input type="date" value={form.prevista} onChange={mudar("prevista")} disabled={!podeOperar} />
        </Campo>
        <div>
          <Campo>
            Data limite
            <input
              type="date"
              value={form.limite}
              onChange={mudar("limite")}
              disabled={!podeOperar}
              aria-describedby={idAjuda}
            />
          </Campo>
          <Ajuda id={idAjuda}>Em branco, vale o da planilha ({dataOuTraco(demanda.prazoPlanilha)}).</Ajuda>
        </div>
      </Campos>
      {podeOperar && mudou && (
        <Campo>
          Motivo da mudança (opcional)
          <textarea value={form.motivo} maxLength={500} onChange={mudar("motivo")} />
        </Campo>
      )}
      {podeOperar && (
        <Acoes>
          <AsyncButton
            silentError
            disabled={!mudou}
            successMessage="Prazos salvos."
            onClick={() =>
              rodar(() =>
                acoes.prazosAmbiente(demanda, { prevista: form.prevista, limite: form.limite, motivo: form.motivo.trim() })
              )
            }
          >
            Salvar prazos
          </AsyncButton>
        </Acoes>
      )}
    </Secao>
  );
}

/**
 * As peças que saíram deste ambiente. As que estão FORA aparecem primeiro (é
 * o que o gestor precisa cobrar); as encerradas continuam listadas com o que
 * aconteceu com elas — voltou, concluiu sem retorno ou foi cancelada.
 */
function PecasDoAmbiente({ pecas, podeDesmembrar, onAbrirPeca, onDesmembrar }) {
  const fora = pecas.filter((p) => p.fora);
  const encerradas = pecas.filter((p) => !p.fora);
  return (
    <Secao aria-label="Peças desmembradas">
      <h3>Peças desmembradas</h3>
      {pecas.length === 0 ? (
        <p className="nota">
          Nenhuma peça desmembrada. Desmembre quando uma peça tiver que andar sozinha pelos setores (ir ao
          acabamento, voltar para a marcenaria) sem o ambiente inteiro ir com ela.
        </p>
      ) : (
        <>
          <p className="nota">
            {fora.length ? plural(fora.length, "peça fora do ambiente", "peças fora do ambiente") : "Nenhuma peça fora agora"}
            {encerradas.length ? ` · ${plural(encerradas.length, "encerrada", "encerradas")}` : ""}
          </p>
          <Lista>
            {[...fora, ...encerradas].map((p) => (
              <ItemLista key={p.id} data-clicavel="sim" onClick={() => onAbrirPeca(p)}>
                <span className="principal">
                  <span className="l1">{p.rotulo}</span>
                  <span className="l2">
                    {[p.setorNome, textoDoPrazo(p.prazoEfetivo).texto].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <PillStatus tom={p.atrasada ? "danger" : TOM_STATUS_PECA[p.status]}>
                  {ROTULO_STATUS_PECA[p.status]}
                </PillStatus>
              </ItemLista>
            ))}
          </Lista>
        </>
      )}
      {podeDesmembrar && (
        <Acoes>
          <button type="button" className="btn btn--secondary" onClick={onDesmembrar}>
            <FiScissors size={14} aria-hidden="true" /> Desmembrar peça
          </button>
        </Acoes>
      )}
    </Secao>
  );
}

function TarefasDoAmbiente({ tarefas, indice, podeCriar, onAbrirTarefa, onNovaTarefa }) {
  return (
    <Secao aria-label="Tarefas do ambiente">
      <h3>Tarefas</h3>
      {tarefas.length === 0 ? (
        <p className="nota">Nenhuma tarefa ligada a este ambiente.</p>
      ) : (
        <Lista>
          {tarefas.map((t) => (
            <ItemLista key={t.id} data-clicavel="sim" onClick={() => onAbrirTarefa(t)}>
              <span className="principal">
                <span className="l1">{t.codigo} · {t.titulo}</span>
                <span className="l2">
                  {indice.colaboradores.get(t.colaborador_id)?.nome || "Sem colaborador"}
                  {t.data_limite ? ` · limite ${formatarDataLocal(t.data_limite)}` : ""}
                </span>
              </span>
              <PillStatus tom={TOM_STATUS_TAREFA[t.status]}>{ROTULO_STATUS_TAREFA[t.status]}</PillStatus>
            </ItemLista>
          ))}
        </Lista>
      )}
      {podeCriar && (
        <Acoes>
          <button type="button" className="btn btn--secondary" onClick={onNovaTarefa}>
            <FiPlus size={14} aria-hidden="true" /> Nova tarefa para este ambiente
          </button>
        </Acoes>
      )}
    </Secao>
  );
}

function DadosDaPlanilha({ demanda, onVerFicha }) {
  const row = demanda.row || {};
  const marceneiro = demanda.marceneiro && !demanda.marceneiro.semMarceneiro ? demanda.marceneiro : null;
  return (
    <Secao aria-label="Planilha">
      <h3>Na planilha</h3>
      <Dados>
        <div>
          <dt>Cliente</dt>
          <dd>{demanda.cliente || "Sem cliente"}</dd>
        </div>
        <div>
          <dt>Pedido</dt>
          <dd>{demanda.pedido || "Sem pedido"}</dd>
        </div>
        <div>
          <dt>Lote</dt>
          <dd>{demanda.lote || "Sem lote"}</dd>
        </div>
        <div>
          <dt>Marceneiro</dt>
          <dd>{marceneiro ? `${marceneiro.nome}${marceneiro.tipo ? ` (${marceneiro.tipo})` : ""}` : "Sem marceneiro"}</dd>
        </div>
        <div>
          <dt>Entrega prevista</dt>
          <dd>{dataOuTraco(toDate(row[F.ENTREGA_PREVISTA]))}</dd>
        </div>
      </Dados>
      <Acoes>
        <button type="button" className="btn btn--ghost" onClick={() => onVerFicha(demanda)}>
          <FiExternalLink size={14} aria-hidden="true" /> Ver a ficha completa da planilha
        </button>
      </Acoes>
    </Secao>
  );
}

/**
 * Props: aberto, demanda (combinada, atual) ou null, indice, estado,
 * tarefasDoAmbiente, pecasDoAmbiente, podeOperar, hoje, acoes
 * (useAcoesDoSetor), onFechar, onAbrirTarefa, onNovaTarefa(demanda),
 * onAbrirPeca(peca), onDesmembrarPeca(demanda), onVerFicha(demanda),
 * onRecarregar.
 */
export default function DetalheAmbiente({
  aberto,
  demanda,
  indice,
  estado,
  tarefasDoAmbiente,
  pecasDoAmbiente = [],
  podeOperar,
  hoje,
  acoes,
  onFechar,
  onAbrirTarefa,
  onNovaTarefa,
  onAbrirPeca,
  onDesmembrarPeca,
  onVerFicha,
  onRecarregar,
}) {
  const { erro, rodar, limpar } = useErroDeAcao();
  const [gatilho, setGatilho] = useState(0);
  const chave = demanda?.chave || null;
  const versao = demanda?.versaoAlocacao;
  const { eventos, erro: erroHistorico } = useEventosDoAmbiente(chave, aberto, `${versao}:${gatilho}`);
  const timeline = useMemo(
    // Comentários do ambiente ficam no bloco Histórico logo acima; repetir
    // aqui mostraria cada um duas vezes no mesmo painel.
    () =>
      demanda
        ? montarTimelineAmbiente(
            demanda.row,
            eventos.filter((e) => e.tipo !== "AMBIENTE_COMENTARIO"),
            tarefasDoAmbiente,
            pecasDoAmbiente
          )
        : [],
    [demanda, eventos, tarefasDoAmbiente, pecasDoAmbiente]
  );
  useEffect(() => {
    if (!aberto) limpar();
  }, [aberto, limpar]);

  const semChave = demanda && !demanda.chave;
  const podeApontar = podeOperar && !semChave;
  const titulo = demanda ? demanda.rotulo || demanda.ambiente || "Ambiente" : "Ambiente";
  const subtitulo = demanda
    ? [demanda.cliente, demanda.lote && `Lote ${demanda.lote}`, demanda.setorNome].filter(Boolean).join(" · ")
    : "";

  return (
    <SlideOverPanel open={aberto} onClose={onFechar} title={titulo} subtitle={subtitulo} width="min(96vw, 640px)">
      {!demanda ? (
        <Vazio>Este ambiente não está mais neste setor (a planilha andou ou ele foi carregado).</Vazio>
      ) : (
        <>
          <ErroDaAcao erro={erro} onRecarregar={onRecarregar} />
          {semChave && (
            <Secao>
              <p className="nota">
                Esta linha da planilha não tem pedido ou ambiente: complete a planilha para distribuir,
                apontar prazos, comentar, desmembrar peças ou ligar tarefas.
              </p>
            </Secao>
          )}
          <Resumo demanda={demanda} indice={indice} hoje={hoje} />
          <AcoesDeStatus demanda={demanda} podeOperar={podeApontar} acoes={acoes} rodar={rodar} />
          <LugarNoSetor demanda={demanda} estado={estado} podeOperar={podeApontar} acoes={acoes} rodar={rodar} />
          <Prazos demanda={demanda} podeOperar={podeApontar} acoes={acoes} rodar={rodar} />
          <PecasDoAmbiente
            pecas={pecasDoAmbiente}
            podeDesmembrar={podeApontar}
            onAbrirPeca={onAbrirPeca}
            onDesmembrar={() => onDesmembrarPeca(demanda)}
          />
          <TarefasDoAmbiente
            tarefas={tarefasDoAmbiente}
            indice={indice}
            podeCriar={podeApontar}
            onAbrirTarefa={onAbrirTarefa}
            onNovaTarefa={() => onNovaTarefa(demanda)}
          />
          {/* Histórico único do ambiente (o mesmo das 16 páginas do Legado). */}
          <HistoricoAmbiente
            row={demanda.row}
            pagina="Mapa da Produção"
            setorId={demanda.setorId}
            onAlterado={() => setGatilho((n) => n + 1)}
          />
          <Secao aria-label="Linha do tempo">
            <h3>Linha do tempo</h3>
            {erroHistorico && <p className="nota">{erroHistorico}</p>}
            <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
              <ListaTimeline itens={timeline} compacto hoje={hoje} vazio="Sem registros ainda." />
            </div>
          </Secao>
          <DadosDaPlanilha demanda={demanda} onVerFicha={onVerFicha} />
        </>
      )}
    </SlideOverPanel>
  );
}
